import React, { useState } from "react";
import {
  PlusOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { Input } from "antd";
import { useTranslation } from "react-i18next";
import {
  filterSessionsBySearch,
  groupSessionsByDate,
} from "util/copilotSessionGroups";
import styles from "./style.module.scss";

const ChatSessionRail = ({
  sessions,
  activeSessionId,
  onSelectSession,
  onNewChat,
  onRenameSession,
  searchQuery,
  onSearchChange,
  displaySessionTitle,
  formatSessionMeta,
  className = "",
  collapsed = false,
  onToggleCollapse,
  showCollapseControl = true,
}) => {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState(null);
  const [draftTitle, setDraftTitle] = useState("");
  const filteredSessions = filterSessionsBySearch(sessions, searchQuery);
  const groups = groupSessionsByDate(filteredSessions);

  const startRename = (session) => {
    setEditingId(session.id);
    setDraftTitle(displaySessionTitle(session.title));
  };

  const cancelRename = () => {
    setEditingId(null);
    setDraftTitle("");
  };

  const commitRename = () => {
    if (editingId == null) return;
    const value = draftTitle.trim();
    const sessionId = editingId;
    cancelRename();
    if (value && onRenameSession) {
      onRenameSession(sessionId, value);
    }
  };

  if (collapsed) {
    return (
      <aside
        className={`${styles.chatSessionRail} ${styles.chatSessionRailCollapsed} ${className}`.trim()}
      >
        <button
          type="button"
          className={`${styles.chatSessionRailIconBtn} ${styles.chatSessionRailIconBtnPrimary}`}
          onClick={onNewChat}
          aria-label={t("cx_gpt.new_chat_btn")}
          title={t("cx_gpt.new_chat_shortcut")}
        >
          <PlusOutlined />
        </button>
        {showCollapseControl && onToggleCollapse ? (
          <div className={styles.chatSessionRailFooter}>
            <button
              type="button"
              className={styles.chatSessionRailCollapseBtn}
              onClick={onToggleCollapse}
              aria-label={t("cx_gpt.history_rail_expand")}
              title={t("cx_gpt.history_rail_expand")}
            >
              <MenuUnfoldOutlined />
            </button>
          </div>
        ) : null}
      </aside>
    );
  }

  return (
    <aside className={`${styles.chatSessionRail} ${className}`.trim()}>
      <div className={styles.chatSessionRailTop}>
        <div className={styles.chatSessionRailHeading}>
          <span className={styles.chatSessionRailTitle}>{t("cx_gpt.history")}</span>
        </div>
        <button
          type="button"
          className={styles.chatSessionNewBtn}
          onClick={onNewChat}
        >
          <PlusOutlined aria-hidden />
          <span>{t("cx_gpt.new_chat_btn")}</span>
        </button>
        <Input
          allowClear
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t("cx_gpt.history_search_placeholder")}
          className={styles.chatSessionSearch}
          aria-label={t("cx_gpt.history_search_placeholder")}
        />
      </div>
      <div className={styles.chatSessionRailScroll}>
        {groups.length === 0 ? (
          <p className={styles.chatSessionEmpty}>{t("cx_gpt.history_empty")}</p>
        ) : (
          groups.map((group) => (
            <div key={group.key} className={styles.chatSessionGroup}>
              <div className={styles.chatSessionGroupLabel}>{t(group.labelKey)}</div>
              {group.sessions.map((session) => {
                const isActive = session.id === activeSessionId;
                if (editingId === session.id) {
                  return (
                    <div
                      key={session.id}
                      className={
                        isActive
                          ? styles.chatSessionItemActive
                          : styles.chatSessionItem
                      }
                    >
                      <Input
                        autoFocus
                        size="small"
                        value={draftTitle}
                        maxLength={256}
                        className={styles.chatSessionRenameInput}
                        placeholder={t("cx_gpt.rename_placeholder")}
                        aria-label={t("cx_gpt.rename")}
                        onChange={(event) => setDraftTitle(event.target.value)}
                        onPressEnter={commitRename}
                        onBlur={commitRename}
                        onKeyDown={(event) => {
                          if (event.key === "Escape") {
                            event.preventDefault();
                            cancelRename();
                          }
                        }}
                      />
                    </div>
                  );
                }
                return (
                  <div
                    key={session.id}
                    className={
                      isActive
                        ? styles.chatSessionItemActive
                        : styles.chatSessionItem
                    }
                  >
                    <button
                      type="button"
                      className={styles.chatSessionItemSelect}
                      onClick={() => onSelectSession(session.id)}
                    >
                      <span className={styles.chatSessionItemTitle}>
                        {displaySessionTitle(session.title)}
                      </span>
                      <span className={styles.chatSessionItemMeta}>
                        {formatSessionMeta(session.updatedAt, group.key)}
                      </span>
                    </button>
                    {onRenameSession ? (
                      <button
                        type="button"
                        className={styles.chatSessionItemRename}
                        onClick={(event) => {
                          event.stopPropagation();
                          startRename(session);
                        }}
                        aria-label={t("cx_gpt.rename")}
                        title={t("cx_gpt.rename")}
                      >
                        <EditOutlined />
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
      {showCollapseControl && onToggleCollapse ? (
        <div className={styles.chatSessionRailFooter}>
          <button
            type="button"
            className={styles.chatSessionRailCollapseBtn}
            onClick={onToggleCollapse}
            aria-label={t("cx_gpt.history_rail_collapse")}
            title={t("cx_gpt.history_rail_collapse")}
          >
            <MenuFoldOutlined />
          </button>
        </div>
      ) : null}
    </aside>
  );
};

export default ChatSessionRail;
